import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { AnalyticsService } from '../../../services/analytics.service';
import Chart from 'chart.js/auto';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {
  @ViewChild('trendCanvas') trendCanvas!: ElementRef;
  summary: any = null;
  chart: any = null;
  error: string = '';
  syncing: boolean = false;
  syncMessage: string = '';
  private retryTimer: any = null;
  
  constructor(private analyticsService: AnalyticsService) {}
  
  ngOnInit() { this.loadAnalytics(); }

  loadAnalytics() {
    this.error = '';
    this.syncMessage = '';
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }

    this.analyticsService.getSummary().subscribe(
      res => { 
        console.log('Summary data:', res);
        this.summary = res;
        this.error = '';
      },
      err => {
        console.error('Error loading summary:', err);
        this.error = 'Failed to load analytics data. Database may not be configured yet.';
        // Set default data to prevent infinite loading
        this.summary = {
          today: {
            total_conversations: 0,
            total_messages: 0,
            faq_matches: 0,
            ai_responses: 0,
            fallback_responses: 0,
            avg_sentiment_score: 0,
            complaints_created: 0
          }
        };
        // Auto-retry after 30 seconds
        this.retryTimer = setTimeout(() => { this.loadAnalytics(); }, 30000);
      }
    );
    
    this.analyticsService.getTrends(7).subscribe(
      res => { 
        console.log('Trends data:', res);
        this.renderChart(res.data || []);
      },
      err => {
        console.error('Error loading trends:', err);
        this.renderChart([]);
      }
    );
  }

  syncAnalytics() {
    this.syncing = true;
    this.syncMessage = '';
    this.analyticsService.syncAnalytics().subscribe(
      res => {
        this.syncing = false;
        this.syncMessage = res.message || 'Sync complete!';
        this.loadAnalytics();
      },
      err => {
        this.syncing = false;
        this.syncMessage = 'Sync failed — check DB connection.';
      }
    );
  }

  renderChart(data: any[]) {
    const labels = data.map(d => {
      const date = new Date(d.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    
    const conversations = data.map(d => d.total_conversations || 0);
    const messages = data.map(d => d.total_messages || 0);
    const faqMatches = data.map(d => d.faq_matches || 0);
    const aiResponses = data.map(d => d.ai_responses || 0);
    
    if (this.chart) this.chart.destroy();
    if (!this.trendCanvas) return;
    
    const ctx = this.trendCanvas.nativeElement.getContext('2d');
    
    // Create gradient fills
    const gradientConv = ctx.createLinearGradient(0, 0, 0, 400);
    gradientConv.addColorStop(0, 'rgba(99, 102, 241, 0.4)');
    gradientConv.addColorStop(1, 'rgba(99, 102, 241, 0.0)');
    
    const gradientMsg = ctx.createLinearGradient(0, 0, 0, 400);
    gradientMsg.addColorStop(0, 'rgba(139, 92, 246, 0.3)');
    gradientMsg.addColorStop(1, 'rgba(139, 92, 246, 0.0)');
    
    const gradientFaq = ctx.createLinearGradient(0, 0, 0, 400);
    gradientFaq.addColorStop(0, 'rgba(16, 185, 129, 0.3)');
    gradientFaq.addColorStop(1, 'rgba(16, 185, 129, 0.0)');
    
    const config: any = {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Conversations',
            data: conversations,
            borderColor: 'rgb(99, 102, 241)',
            backgroundColor: gradientConv,
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointRadius: 5,
            pointHoverRadius: 7,
            pointBackgroundColor: 'rgb(99, 102, 241)',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointHoverBackgroundColor: 'rgb(99, 102, 241)',
            pointHoverBorderColor: '#fff',
            pointHoverBorderWidth: 3
          },
          {
            label: 'Messages',
            data: messages,
            borderColor: 'rgb(139, 92, 246)',
            backgroundColor: gradientMsg,
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(139, 92, 246)',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            borderDash: [5, 5]
          },
          {
            label: 'FAQ Matches',
            data: faqMatches,
            borderColor: 'rgb(16, 185, 129)',
            backgroundColor: gradientFaq,
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(16, 185, 129)',
            pointBorderColor: '#fff',
            pointBorderWidth: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            align: 'end',
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                size: 12,
                weight: '600',
                family: 'Inter, system-ui, sans-serif'
              },
              color: '#475569'
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            padding: 12,
            cornerRadius: 8,
            titleFont: {
              size: 13,
              weight: '600'
            },
            bodyFont: {
              size: 12
            },
            bodySpacing: 6,
            borderColor: 'rgba(99, 102, 241, 0.3)',
            borderWidth: 1,
            displayColors: true,
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                label += context.parsed.y;
                return label;
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              font: {
                size: 11,
                weight: '500'
              },
              color: '#64748b'
            },
            border: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              color: 'rgba(148, 163, 184, 0.1)',
              drawBorder: false
            },
            ticks: {
              font: {
                size: 11,
                weight: '500'
              },
              color: '#64748b',
              padding: 10,
              callback: function(value) {
                return Number.isInteger(value) ? value : '';
              }
            },
            border: {
              display: false,
              dash: [5, 5]
            }
          }
        },
        animation: {
          duration: 1000,
          easing: 'easeInOutQuart'
        }
      }
    };
    
    this.chart = new Chart(ctx, config);
  }
}
